import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { DEALS_QUERY_KEY } from './useDeals';
import { type ApiDealResponse } from './types';

/**
 * Corpo do POST /api/deals (CreateDealCommand). `legalStatus` é numérico (bind de enum por número,
 * igual ao UpdateDeal). O backend gera o Code, nasce em Originação com status Ativo, IsPriority=false.
 */
export interface CreateDealBody {
  codename: string;
  legalStatus: number;
  folderId: string | null;
  targetCompanyId: string | null;
  originatorIdSnapshot: string | null;
  originatorPersonIdSnapshot: string | null;
  internalOriginatorId: string | null;
  leadUserId: string;
  investmentValueBRL: number | null;
  thesisSummary: string | null;
  thesisIds: number[];
  teamUserIds: string[];
  tags: string[];
  /** Data de entrada no funil (ISO UTC). null → servidor usa "agora" (item 7). */
  initialEntryAtUtc: string | null;
}

/** Formato do formulário de +Novo Deal (NewDealModal) — entrada da montagem do corpo. */
export interface NewDealForm {
  codename: string;
  legalStatus: number;
  investmentValue: string;
  thesisSummary: string;
  leadUserId: string;
  thesisIds: number[];
  teamUserIds: string[];
  targetCompany: { id: string; name: string } | null;
  externalOriginator: { id: string; name: string } | null;
  person: { id: string; name: string } | null;
  isInternalOriginator: boolean;
  internalOriginatorId: string | null;
  /** Data de entrada (YYYY-MM-DD). Vazio → servidor usa "agora". */
  initialEntryAt: string;
}

/**
 * Monta o corpo do POST a partir do formulário. Pura (testável sem render). Regras: originação
 * interna zera o snapshot externo e usa internalOriginatorId; valor vira número só com dígitos;
 * campos vazios viram null; tags entram vazias na criação (editáveis na ficha).
 */
export function buildCreateDealBody(form: NewDealForm): CreateDealBody {
  const digits = form.investmentValue.replace(/\D/g, '');
  return {
    codename: form.codename.trim(),
    legalStatus: form.legalStatus,
    folderId: null,
    targetCompanyId: form.targetCompany?.id ?? null,
    originatorIdSnapshot: form.isInternalOriginator ? null : (form.externalOriginator?.id ?? null),
    originatorPersonIdSnapshot: form.person?.id ?? null,
    internalOriginatorId: form.isInternalOriginator ? form.internalOriginatorId : null,
    leadUserId: form.leadUserId,
    investmentValueBRL: digits ? Number(digits) : null,
    thesisSummary: form.thesisSummary.trim() || null,
    thesisIds: form.thesisIds,
    teamUserIds: form.teamUserIds,
    tags: [],
    initialEntryAtUtc: form.initialEntryAt
      ? new Date(`${form.initialEntryAt}T00:00:00Z`).toISOString()
      : null,
  };
}

/**
 * Cria um deal (POST /api/deals). Em sucesso invalida a listagem — o deal novo aparece na coluna
 * Originação do kanban — e dá toast. Erros de validação (400) caem no toast genérico; o form já
 * barra o essencial (codinome + empresa) antes de enviar.
 */
export function useCreateDeal() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (body: CreateDealBody) =>
      apiFetch<ApiDealResponse>('/api/deals', { method: 'POST', body }),
    onSuccess: (resp) => {
      void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      toast(`Deal criado: ${resp.code}`);
    },
    onError: () => {
      toast('Não foi possível criar o deal. Confira os campos e tente de novo.', 'danger');
    },
  });
}
